/**
 * When is a record's audio actually playable?
 *
 * `records_metadata.file_uri` holds two different kinds of value, and only one
 * of them is ours. `r2://…` is the archived copy, streamable through
 * `GET /records/:id/audio` with our own auth. Anything else is the raw
 * CRM/telephony URL that ingest wrote before the archive step ran
 * (`apps/workflows-api/src/workers/ingest/pipeline-router.ts`): cross-origin,
 * usually requiring the provider's own credentials, and presigned for minutes.
 *
 * Handing that raw URL to a media element is what produced the reported
 * failure — the player errors on a freshly imported record and starts working
 * after a page reload, once the archive copy has landed and `file_uri` has been
 * rewritten. The backend already learned this rule in v2.7.3 (trust `file_uri`
 * only when it starts with `r2://`, re-resolve otherwise); this module applies
 * it at the one place that never got it, the player.
 */

/** The only `file_uri` shape the player may act on. */
export const DURABLE_AUDIO_PREFIX = 'r2://';

/** Poll cadence while the archive copy is still being written. */
export const AUDIO_POLL_INTERVAL_MS = 5_000;

/**
 * How long after ingest the archive copy is still expected to appear.
 *
 * Archiving runs inside the record's own pipeline, so in the normal case it
 * lands within seconds and this bound is never reached. Past it, `file_uri`
 * still holding a raw URL means `waitForMediaStep` deliberately kept it
 * because the R2 upload failed — polling will never produce a file, so we stop
 * and say so rather than spin a loading skeleton forever.
 */
export const AUDIO_ARCHIVE_GRACE_MS = 10 * 60_000;

/** Our own archived copy — the only URI the player may load. */
export function isDurableAudioUri(fileUri?: string | null): boolean {
  return typeof fileUri === 'string' && fileUri.startsWith(DURABLE_AUDIO_PREFIX);
}

/**
 * Media exists upstream but our copy has not landed yet — worth waiting for.
 *
 * False for a record with no media at all: a record can sit in
 * `awaiting_media` for up to 24 h, which is not something to poll through.
 * Also false once the grace window has passed (see above), and when the
 * timestamp cannot be read at all — an unbounded wait is a worse failure than
 * an honest "unavailable", and the detail mapper always supplies a timestamp,
 * so an unparsable one means data we do not understand.
 */
export function isAudioArchivePending(
  fileUri: string | null | undefined,
  createdAt: string | null | undefined,
  nowMs: number
): boolean {
  if (!fileUri) return false;
  if (isDurableAudioUri(fileUri)) return false;

  const createdMs = Date.parse(createdAt ?? '');
  if (Number.isNaN(createdMs)) return false;

  return nowMs - createdMs < AUDIO_ARCHIVE_GRACE_MS;
}

/**
 * `refetchInterval` for the record detail query: re-poll only while the
 * archive copy is still expected, so the player appears on its own instead of
 * requiring the reload the user had to do by hand.
 */
export function audioRefetchInterval(
  fileUri: string | null | undefined,
  createdAt: string | null | undefined,
  nowMs: number
): number | false {
  return isAudioArchivePending(fileUri, createdAt, nowMs)
    ? AUDIO_POLL_INTERVAL_MS
    : false;
}

/**
 * What the panel should show in the audio slot.
 *
 * - `ready` — a URL the player can load
 * - `preparing` — the file is on its way (archive pending, or blob in flight);
 *   the slot is reserved so the transcript below does not jump when the player
 *   mounts
 * - `unavailable` — nothing is coming; say so instead of loading forever
 */
export type AudioState = 'ready' | 'preparing' | 'unavailable';
